import { useState, useCallback, useRef, useEffect } from 'react';
import {
  isSlotCoreInstalled,
  getInstalledVersion,
  checkForUpdate,
  downloadAndInstallSlotCore,
} from '../services/slot-core';
import { deleteDirectory } from '../services/FileSystemService';
import { refreshProjectFiles } from '../utils/project-files';
import { SLOT_CORE_DEST_FOLDER } from '../constants/slot-core';
import type { SlotCoreProgress } from '../services/slot-core';

export type SlotCoreStatus =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'extracting'
  | 'complete'
  | 'error';

export interface UseSlotCoreInstallReturn {
  status: SlotCoreStatus;
  progress: SlotCoreProgress | null;
  error: string | null;
  hasUpdate: boolean;
  installedVersion: string | null;
  install: () => Promise<void>;
  update: () => Promise<void>;
  checkUpdate: () => Promise<void>;
  cancel: () => void;
  retry: () => void;
  dismiss: () => void;
}

export function useSlotCoreInstall(): UseSlotCoreInstallReturn {
  const [status, setStatus] = useState<SlotCoreStatus>('idle');
  const [progress, setProgress] = useState<SlotCoreProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const oldShaRef = useRef<string | null>(null);
  const newShaRef = useRef<string | null>(null);

  // Load installed version on mount
  useEffect(() => {
    getInstalledVersion().then((v) => {
      if (v) setInstalledVersion(v.sha.slice(0, 7));
    });
  }, []);

  const performInstall = useCallback(async (isUpdate: boolean) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);

    // Track old SHA for update display
    if (isUpdate) {
      const current = await getInstalledVersion();
      oldShaRef.current = current?.sha.slice(0, 7) || null;
    }

    setStatus('downloading');

    try {
      // Clean up partial/existing install if updating
      if (isUpdate) {
        try {
          await deleteDirectory(SLOT_CORE_DEST_FOLDER, true);
        } catch {
          // Ignore — may not exist
        }
      }

      const result = await downloadAndInstallSlotCore(
        (p) => {
          setProgress(p);
          if (p.phase === 'downloading') setStatus('downloading');
          else if (p.phase === 'extracting' || p.phase === 'writing') setStatus('extracting');
        },
        controller.signal,
      );

      if (result.success) {
        const shortSha = result.sha?.slice(0, 7) || null;
        newShaRef.current = shortSha;
        setInstalledVersion(shortSha);
        setHasUpdate(false);
        setStatus('complete');

        // Refresh project files so slot-core/ shows in project panel
        try {
          await refreshProjectFiles();
        } catch {
          // Non-critical
        }

        // Notify badge to re-check
        window.dispatchEvent(new CustomEvent('slot-core:install-complete'));
      } else {
        setStatus('error');
        setError(result.error || 'Installation failed');
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setStatus('idle');
        return;
      }
      setStatus('error');
      setError((err as Error).message);
    }
  }, []);

  const install = useCallback(async () => {
    // Skip if already installed
    if (await isSlotCoreInstalled()) {
      setStatus('complete');
      const v = await getInstalledVersion();
      if (v) setInstalledVersion(v.sha.slice(0, 7));
      return;
    }
    await performInstall(false);
  }, [performInstall]);

  const update = useCallback(async () => {
    // Check if there's actually a new version before downloading
    setStatus('checking');
    try {
      const result = await checkForUpdate();
      if (!result.hasUpdate) {
        setInstalledVersion(result.localSha?.slice(0, 7) || installedVersion);
        setHasUpdate(false);
        setStatus('complete');
        setProgress({ phase: 'complete', message: 'Already up to date' });
        return;
      }
    } catch {
      // If check fails, proceed with download anyway (force update)
    }

    await performInstall(true);
  }, [performInstall, installedVersion]);

  const checkUpdateFn = useCallback(async () => {
    setStatus('checking');
    try {
      const result = await checkForUpdate();
      setHasUpdate(result.hasUpdate);
      if (result.localSha) {
        setInstalledVersion(result.localSha.slice(0, 7));
      }
      setStatus('idle');
    } catch {
      setStatus('error');
      setError('Failed to check for updates');
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setStatus('idle');
    setProgress(null);
  }, []);

  const retry = useCallback(() => {
    setError(null);
    performInstall(false);
  }, [performInstall]);

  const dismiss = useCallback(() => {
    setStatus('idle');
    setProgress(null);
    setError(null);
  }, []);

  return {
    status,
    progress,
    error,
    hasUpdate,
    installedVersion,
    install,
    update,
    checkUpdate: checkUpdateFn,
    cancel,
    retry,
    dismiss,
  };
}

/** Short SHA references for update toast display */
export function useSlotCoreShaRefs() {
  return { oldShaRef: useRef<string | null>(null), newShaRef: useRef<string | null>(null) };
}
